import * as React from 'react';
import axios, { CancelTokenSource } from 'axios';
import clsx from 'clsx';
import { useHistory } from "react-router-dom";
import themePrimary from '../../styles/themePrimary';
import { Typography, makeStyles, useMediaQuery } from '@material-ui/core';
import { UserStateContext } from '../../providers/UserStateContextProvider';
import { CheckOutStateContext } from '../../providers/CheckOutStateContextProvider';
import MiniButton, { MiniButtonProps } from '../common/Buttons/MiniButton';
import ViewReceiptDialog, { ViewReceiptDialogProps } from './ViewReceiptDialog';
import { String_ReviewOutstandingChecks_ViewReceiptButton, String_System_GeneralError, String_System_ConnectionError, 
    String_MultipleChecks_CheckNumberLabel, String_System_View, String_MultipleReceipt_ReceiptLabel,
    String_ReviewOutstandingChecks_Instructions } from '../../utilities/StringHelper';
import ErrorDialog, { ErrorDialogProps } from '../common/ErrorDialog';
import Loader from '../common/Loader';
import { ElementDimensions } from '../../styles/ElementDimensions';
import { CheckSummary } from '../../models/common/CheckSummary';
import SignalR, { CheckMessageType, CheckNotificationType, OnNotificationReceivedHandler } from '../common/SignalR';
import { ErrorCode } from '../../models/common/ErrorCode';
import { CheckOutType } from '../../models/common/CheckOutType';
import { POSType } from '../../models/common/POSType';
import { ListChecksRequest } from '../../models/postcheckout/ListChecksRequest';
import { ListChecksResponse } from '../../models/postcheckout/ListChecksResponse';

const useStyles = makeStyles({
    assignTextPaid: {
        fontSize: '1.2em',
        marginBottom: ElementDimensions.margin10 + 'px',
        color: '#0a9100',
    },
    assignTextNotPaid: {
        fontSize: '1.2em',
        marginBottom: ElementDimensions.margin10 + 'px',
        color: '#bf0000',
    },
    assignLabelText: {
        textAlign: 'left',
    },
    assignValueText: {
        textAlign: 'right',
    },
    checkContainer: {
        borderRadius: '6px',
        backgroundColor: themePrimary.palette.grey[200],
        margin: ElementDimensions.margin10 + 'px',
    },
    checkNumberLabel: {
        marginTop: 'auto',
        marginBottom: 'auto',
    },
    details: {
        position: 'absolute',
        top: (ElementDimensions.appBarHeight) + 'px',
        bottom: '50px',
        left: 0,
        right: 0,
        width: '100%',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    detailsLandscape: {
        position: 'absolute',
        top: '0px',
        bottom: '0px',
        left: 'calc(100vw *.3)',
        width: 'calc(100vw *.7)',
        overflowY: 'scroll',
        overflowX: 'hidden',
    },
    footerNoButtons: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        height: '50px',
        padding: ElementDimensions.padding10 + 'px ' + '0px ' + ElementDimensions.padding10 + 'px 0px',
        width: '100%',
        backgroundColor: themePrimary.palette.grey[200],
    },
    gridContainer: {
        display: 'flex',
        width: '100%',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '70% 30%',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: ElementDimensions.margin5 + 'px',
        marginBottom: ElementDimensions.margin5 + 'px',
        width: 'calc(100% - 2em)',
    },
    headerContainer: {
        padding: ElementDimensions.padding10 + 'px',
    },
    headerGrid: {
        display: 'grid',
        gridTemplateColumns: 'calc(70vw - 20px) calc(30vw - 20px)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    headerGridLandscape: {
        display: 'grid',
        gridTemplateColumns: 'calc(30vw - 10px) calc(70vw - 10px)',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    headerViewCheckButton: {
        textAlign: 'right',
    },
    headerViewReceiptButton: {
        textAlign: 'right',
    },
    headerTextLeft: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
    },
    headerTextRight: {
        fontSize: '1rem',
        lineHeight: '2.2rem',  
        textAlign: 'right',
    },
    instructionContainer: {
        textAlign: 'center',
        marginLeft: ElementDimensions.margin5 + 'px',
        marginRight: ElementDimensions.margin5 + 'px',
        paddingTop: ElementDimensions.padding10 + 'px ',
    },
    instructionText: {
      marginBottom: ElementDimensions.margin10 + 'px',
    },
    loadingContainer: {
        margin: ElementDimensions.margin20 + 'px',
        textAlign: 'center',
    },
    logoContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        width: '100%',
        textAlign: 'center',
        paddingTop: ElementDimensions.padding10 + 'px',
        paddingBottom: ElementDimensions.padding10 + 'px',
    },
    outsideGridContainer: {
        display: 'flex',
        width: '100%',
    },
    outsideGrid: {
        display: 'grid',
        marginLeft: 'auto',
        marginRight: 'auto',
        marginTop: ElementDimensions.margin5 + 'px',
        marginBottom: ElementDimensions.margin5 + 'px',
        width: '100%',
    },
    phoneNumber: {
        fontSize: '1.2em',
        marginBottom: ElementDimensions.margin10 + 'px',
    },
    root: {
        height: '100%',
        overflow: 'hidden',
    },
    rootLandscape: {
        marginTop: '0',
        width: 'calc(100vw *.7)',
    },
});

const ReviewChecks = (props: any) => {
    const isPortrait = true;
    let history = useHistory();
    const [checks, setChecks] = React.useState<CheckSummary[]>();
    const [viewReceiptPaymentId, setViewReceiptPaymentId] = React.useState<string | null>(null);
    const [viewReceiptDialogOpen, setViewReceiptDialogOpen] = React.useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [loaded, setLoaded] = React.useState<boolean>(false);
    const [loadError, setLoadError] = React.useState<boolean>(false);
    const [openChecks, setOpenChecks] = React.useState<boolean>(false);
    const userState = React.useContext(UserStateContext);
    const checkOutState = React.useContext(CheckOutStateContext);
    const classes = useStyles({ primary_main_color: checkOutState.state.skin.primary_main_color, 
        primary_light_color: checkOutState.state.skin.primary_light_color, primary_dark_color: checkOutState.state.skin.primary_dark_color });

    let paymentId: string | null = null;

    const signalRNotificationReceivedHandler = (type: CheckNotificationType) => {
        paymentId = history.location.state.paymentId;

        if (paymentId !== null) {
            if (type === CheckNotificationType.AmountClaimed) {
                loadData(axios.CancelToken.source());
            }
            else if (type === CheckNotificationType.PaymentCompleted) {
                loadData(axios.CancelToken.source());
            }
            else if (type === CheckNotificationType.CheckClosed) {
                loadData(axios.CancelToken.source());
            }
            else if (type === CheckNotificationType.ClaimExpired) {
                loadData(axios.CancelToken.source());
            }
        }
    }

    React.useEffect(() => {
        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();

        paymentId = history.location.state.paymentId;

        if (paymentId !== null) {
            loadData(source); 
        }        
        
        return () => {
            source.cancel();
        };
    }, []);

    const loadData = (cancelTokenSource: CancelTokenSource) => {
        const request: ListChecksRequest = {
            user_id: userState.state.userId!!,
            table_id: checkOutState.state.tableId,
            check_please_payment_id: paymentId!!,
        };

        let url: string | undefined = process.env.REACT_APP_CHECKOUT_API_URL + "/location/" + checkOutState.state.restaurantId + "/checks"

        const headers = {
            'X-API-Key': checkOutState.state.restaurantApiKey,
            'location_id': checkOutState.state.restaurantId
        }

        axios.post(url, request, { headers, cancelToken: cancelTokenSource.token })
        .then(function(response) {
            let r = response.data as ListChecksResponse;

            if (r.success === true) {
                let goToReceipt: boolean = false;

                if (checkOutState.state.checkOutType == CheckOutType.ScanReceiptToPay) {
                    var receiptCheck = null;

                    if (r.checks.length > 0) {
                        r.checks.forEach( c => {
                            if (c.id == checkOutState.state.checks!![0].id) {
                                receiptCheck = c
                            }
                        });

                        if (receiptCheck !== null) {
                            r.checks = []
                            r.checks.push(receiptCheck)
                        }
                    }
                }

                if (r.checks.length === 0) {
                    goToReceipt = true;
                }
                else if (r.checks.length === 1) {
                    let paidCheckPleasePayments = 0;
                    if (r.checks[0].payments !== null) {
                        r.checks[0].payments.forEach(p => {
                            if (p.user_id === userState.state.userId) {
                                paidCheckPleasePayments += 1
                            }
                        });

                        if (paidCheckPleasePayments === 1 && r.checks[0].amount_due === 0) {
                            goToReceipt = true;
                        }
                    }
                    else {
                        goToReceipt = true;
                    }

                    if (paidCheckPleasePayments === 1 && (r.checks[0].amount_due === 0 || r.checks[0].payments === null)) {
                        goToReceipt = true;
                    }
                    else {
                        let paidCheckPleasePayments = 0;
                        r.checks[0].payments!!.forEach(p => {
                            if (p.user_id === userState.state.userId) {
                                paidCheckPleasePayments += 1
                            }
                        });

                        if (paidCheckPleasePayments === 1 && r.checks[0].amount_due <= 0) {
                            goToReceipt = true;
                        }
                    }
                }
                else {
                    let paidCheckPleasePayments = 0;
                    let outstandingBalance = false;

                    r.checks.forEach(c => {
                        if (c.payments !== null) {
                            c.payments.forEach(p => {
                                if (p.user_id === userState.state.userId) {
                                    paidCheckPleasePayments += 1
                                }
                            });
                        }

                        if (c.amount_due > 0) {
                            outstandingBalance = true;
                        }
                    });

                    if (!outstandingBalance && paidCheckPleasePayments === 1) {
                        goToReceipt = true;
                    }                    
                }

                r.checks.forEach(c => {
                    if (c.amount_due > 0) {
                        setOpenChecks(true);
                    }
                });

                if (goToReceipt) {
                    history.push({
                        pathname: '/postcheckout/receipt',
                        state: { 
                            paymentId: paymentId,
                        }
                    }); 
                }
                else {
                    setChecks(r.checks);
                    setLoaded(true);    
                }
            }
            else {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
                setErrorDialogOpen(true);
                setLoadError(true);
            }
        })
        .catch(function(error) {
            if (error.response) {
                setErrorMessage(String_System_GeneralError.find(s => s.key === userState.state.locale)!!.value);
            }
            else {
                setErrorMessage(String_System_ConnectionError.find(s => s.key === userState.state.locale)!!.value);
            }
    
            setErrorDialogOpen(true);
            setLoadError(true);
        });
    };

    const handleViewReceiptButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setViewReceiptPaymentId(event.currentTarget.value);
        setViewReceiptDialogOpen(true);
    }

    const viewReceiptButtonProps: MiniButtonProps = {
        value:'',
        disabled: false,
        label: String_ReviewOutstandingChecks_ViewReceiptButton.find(s => s.key === userState.state.locale)!!.value,
        onClickHandler: handleViewReceiptButtonClick,
    }

    const handleViewReceiptDialogCloseClick = (event: React.MouseEvent<HTMLElement>) => {
        setViewReceiptDialogOpen(false);
    }

    const viewReceiptDialogProps: ViewReceiptDialogProps = {
        paymentId: viewReceiptPaymentId!!,
        open: viewReceiptDialogOpen,
        onCloseHandler: handleViewReceiptDialogCloseClick,
    }

    const handleViewCheckButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        
        let check = checks!!.find(c => c.number === event.currentTarget.value);
        if (check !== null && check !== undefined) {
            history.push({
                pathname: '/checkout/check',
                state: { 
                    checkSummary: check,
                }
            });  
        }
    }

    const viewCheckButtonProps: MiniButtonProps = {
        value:'',
        disabled: false,
        label: String_System_View.find(s => s.key === userState.state.locale)!!.value,
        onClickHandler: handleViewCheckButtonClick,
    }

    const handleErrorDialogClose = (event: React.MouseEvent<HTMLElement>) => {
        setErrorDialogOpen(false);
        setLoadError(false);

        const cancelToken = axios.CancelToken;
        const source = cancelToken.source();
        loadData(source);
    }

    const errorDialogProps: ErrorDialogProps = {
        open: errorDialogOpen,
        message: errorMessage,
        tryAgain: true,
        onCloseHandler: handleErrorDialogClose,
    }

    const signalRInitializedHandler = () => {
        
    }

    return (
        <div className={clsx(classes.root, {
            [classes.rootLandscape]: !isPortrait,
        })}>
            {!loaded && !loadError &&
                <div className={classes.loadingContainer}>
                    {!loaded &&
                        <Loader />                    
                    }
                </div>
            }

            {loaded && !loadError && (checks !== null && checks !== undefined) &&
                <div>
                    <div className={isPortrait === true ? classes.details : classes.detailsLandscape}>
                        <div className={classes.instructionContainer}>
                            {openChecks &&
                                <Typography variant="h6" className={classes.instructionText}>
                                    {String_ReviewOutstandingChecks_Instructions.find(s => s.key === userState.state.locale)!!.value}
                                </Typography>
                            }
                        </div>
                        {
                            checks.map((check, index) => {
                                return (
                                    <div key={'pairchecks-outer-container-' + check.number} className={classes.checkContainer}>
                                        <div className={classes.headerContainer} key={'pairchecks-header-outer-container-' + check.number}>
                                            <div className={isPortrait ? classes.headerGrid : classes.headerGridLandscape} key={'pairchecks-header-inner-container-' + check.number}>
                                                <Typography variant="h6" key={'pairchecks-header-check-number-' + check.number} className={classes.checkNumberLabel}>
                                                    {checkOutState.state.posType !== POSType.Clear &&
                                                        String_MultipleChecks_CheckNumberLabel.find(s => s.key === userState.state.locale)!!.value
                                                    } 
                                                    {check.number}
                                                </Typography>
                                                {check.amount_due > 0 &&
                                                    <div className={classes.headerViewCheckButton} key={'pairchecks-view-check-button-container-' + check.number}>
                                                        <MiniButton
                                                            key={'pairchecks-view-check-button-' + check.number}
                                                            value={check.number}
                                                            disabled={viewCheckButtonProps.disabled}
                                                            label={viewCheckButtonProps.label}
                                                            onClickHandler={viewCheckButtonProps.onClickHandler} />
                                                    </div>
                                                }
                                            </div>
                                        </div>
                                        
                                        {check.payments !== null && check.payments.length > 0 &&
                                            check.payments.map((payment, index2) => {
                                                return (
                                                    <div>
                                                        {payment.user_id === userState.state.userId &&
                                                            <div className={classes.headerContainer} key={'pairchecks-header-inner-container-' + check.number}>
                                                                <div className={classes.headerGrid} key={'inner-container-' + check.id + '-' + payment.payment_id.toString()}>
                                                                    <Typography variant="h6" className={classes.headerTextLeft} key={'receipt-' +  + check.id + '-' + payment.toString()}>
                                                                        {String_MultipleReceipt_ReceiptLabel.find(s => s.key === userState.state.locale)!!.value} {(index2 + 1).toString()}
                                                                    </Typography>
                                                                    <div className={classes.headerViewReceiptButton} key={'receipt-button-container-' +  + check.id + '-' + payment.toString()}>
                                                                        <MiniButton
                                                                            key={'receipt-button-' + check.id + '-' + payment.payment_id.toString()}
                                                                            value={payment.payment_id.toString()}
                                                                            disabled={viewReceiptButtonProps.disabled}
                                                                            label={viewReceiptButtonProps.label}
                                                                            onClickHandler={viewReceiptButtonProps.onClickHandler} />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        }
                                                    </div>
                                                );
                                            })
                                        }
                                    </div>
                                );
                            })
                        }
                        
                        <ViewReceiptDialog 
                            paymentId={viewReceiptDialogProps.paymentId}
                            open={viewReceiptDialogProps.open}
                            onCloseHandler={viewReceiptDialogProps.onCloseHandler} />

                    </div>
                </div>
            }

            <ErrorDialog 
                open={errorDialogProps.open}
                message={errorDialogProps.message}
                tryAgain={errorDialogProps.tryAgain}
                onCloseHandler={errorDialogProps.onCloseHandler} />

            {checkOutState.state.checks !== null && 
                <SignalR 
                    oldChecks={null}
                    messageType={CheckMessageType.Subscribe}
                    load={true}
                    refreshCheck={true}
                    onNotificationReceived={signalRNotificationReceivedHandler}
                    onInitialized={signalRInitializedHandler} />
            }

        </div>
    );
};

export default ReviewChecks;